import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";

import AutocompleteWrapper from "components/AutocompleteWrapper";
import Icon from "components/Icon";
import { Typography } from "@material-ui/core";

import { fetchCategories, fetchSingleCategory } from "store/category/actions";
import * as api from "lib/api/index";
import API_RESOURCES from "lib/api/resources";

const CategoryFilter = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const [searchValue, setSearchValue] = useState("");
  const [value, setValue] = useState(null);
  const hasLoadedForClientRef = useRef(null);

  const { list: categoryFilterList, selected: selectedCategory } = useSelector(
    ({ categoryReducer }) => categoryReducer
  );

  const searchParams = queryString.parse(location.search);
  const categoryIdFromQuery = searchParams.category;
  const clientIdFromQuery = searchParams.clientId;

  useEffect(() => {
    if (clientIdFromQuery && hasLoadedForClientRef.current !== clientIdFromQuery) {
      dispatch(fetchCategories({ clientId: clientIdFromQuery, limit: 1000 }));
      hasLoadedForClientRef.current = clientIdFromQuery;
    } else if (!clientIdFromQuery) {
      hasLoadedForClientRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFromQuery]);

  const options = useMemo(() => {
    if (!selectedCategory || !categoryIdFromQuery) {
      return categoryFilterList;
    }
    if (selectedCategory._id === categoryIdFromQuery) {
      const existsInList = categoryFilterList.some(
        (cat) => cat._id === selectedCategory._id
      );
      if (!existsInList) {
        return [selectedCategory, ...categoryFilterList];
      }
    }

    return categoryFilterList;
  }, [categoryFilterList, selectedCategory, categoryIdFromQuery]);

  // Ensure URL has correct clientId when category comes from URL or changes externally
  useEffect(() => {
    const ensureClientIdMatchesCategory = async () => {
      if (!categoryIdFromQuery) return;

      try {
        const response = await api.get(`${API_RESOURCES.CATEGORIES}/${categoryIdFromQuery}`);
        const cat = response && response.data ? response.data : null;
        if (!cat || !cat.clientId) return;

        const catClientId = (typeof cat.clientId === 'object' ? (cat.clientId._id || cat.clientId) : cat.clientId)?.toString();
        const currentClientId = clientIdFromQuery?.toString();

        if (catClientId && catClientId !== currentClientId) {
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.set('clientId', catClientId);
          history.push({ pathname: location.pathname, search: newSearchParams.toString() });
        }
      } catch (e) {
        // ignore
      }
    };

    ensureClientIdMatchesCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFromQuery]);

  // Handle category selection and set clientId
  const handleCategorySelect = async (selectedCategoryId) => {
    if (!selectedCategoryId) {
      // Clear category
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('category');
      history.push({
        pathname: location.pathname,
        search: searchParams.toString()
      });
      return;
    }

    // Find the selected category in the available options
    let selectedCategoryFromList = options.find(
      (cat) => {
        const catId = cat._id?.toString();
        const selectedId = selectedCategoryId?.toString();
        return catId === selectedId;
      }
    );

    // If not found in options, try selectedCategory from store
    if (!selectedCategoryFromList && selectedCategory) {
      const selectedId = selectedCategoryId?.toString();
      const storeCatId = selectedCategory._id?.toString();
      if (storeCatId === selectedId) {
        selectedCategoryFromList = selectedCategory;
      }
    }

    // If still not found, fetch the category from API
    let categoryToUse = selectedCategoryFromList;
    if (!categoryToUse) {
      try {
        const response = await api.get(`${API_RESOURCES.CATEGORIES}/${selectedCategoryId}`);
        if (response && response.data) {
          categoryToUse = response.data;
          dispatch(fetchSingleCategory(selectedCategoryId));
        }
      } catch (error) {
        console.error('Error fetching category:', error);
      }
    }

    // Get clientId from category
    let categoryClientId = null;
    if (categoryToUse && categoryToUse.clientId) {
      categoryClientId = typeof categoryToUse.clientId === 'object' 
        ? (categoryToUse.clientId._id || categoryToUse.clientId)
        : categoryToUse.clientId;
      categoryClientId = categoryClientId?.toString();
    }

    // Update URL with both category and clientId
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('category', selectedCategoryId);
    
    if (categoryClientId) {
      newSearchParams.set('clientId', categoryClientId);
    }

    history.push({
      pathname: location.pathname,
      search: newSearchParams.toString()
    });
  };

  const onChange = (value) => {
    setSearchValue(value);
  };

  useEffect(() => {
    if (searchValue && searchValue.length >= 3) {
      const queryParams = { search: searchValue };
      if (clientIdFromQuery) {
        queryParams.clientId = clientIdFromQuery;
      }
      dispatch(fetchCategories(queryParams));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchValue, clientIdFromQuery]);

  useEffect(() => {
    if (categoryIdFromQuery) {
      setValue(categoryIdFromQuery);
    } else {
      setValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <AutocompleteWrapper
      value={value}
      size={"large"}
      allowClear
      onSearch={onChange}
      onSelect={handleCategorySelect}
      placeholder={'Category'}
      optionValueSelector={"_id"}
      optionLabelSelector={"name"}
      options={options}
      clearIcon={<Icon name={"cancel"} fontSize={20} color={"black"} />}
      suffixIcon={
        <Icon
          name={"dropdown-arrow"}
          type={"solid"}
          fontSize={16}
          color={"rgba(0, 0, 0, 0.54)"}
        />
      }
      notFoundContent={<Typography>No results to display.</Typography>}
    />
  );
};

export default CategoryFilter;
